import React, { useState } from 'react';
import AppHeader from '../../../components/AppHeader.tsx';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faCalendar,
  faCheckCircle,
  faClock,
  faDownload,
  faEdit,
  faExclamationTriangle,
  faFilter,
  faPlane,
  faPlus,
  faSearch,
  faTimesCircle,
  faTrash,
  faUser,
} from '@fortawesome/free-solid-svg-icons';

const VacationsPage: React.FC = () => {
  const [searchTerm, setSearchTerm] = useState('');
  const [filterStatus, setFilterStatus] = useState('all');
  const [_, setShowAddVacation] = useState(false);

  const breadcrumb = [{ label: 'Zaměstnanci' }, { label: 'Dovolené a absence' }];

  const staff = [
    { id: '1', name: 'Anna Svobodová', role: 'Kadeřnice' },
    { id: '2', name: 'Petr Novák', role: 'Kadeřník' },
    { id: '3', name: 'Marie Krásná', role: 'Kadeřnice' },
    { id: '4', name: 'Jana Nová', role: 'Kosmetička' },
  ];

  const vacations = [
    {
      id: '1',
      staffId: '1',
      type: 'vacation',
      title: 'Letní dovolená',
      startDate: '2024-07-15',
      endDate: '2024-07-28',
      days: 14,
      status: 'approved',
      reason: 'Rodinná dovolená u moře',
      requestedAt: '2024-06-01',
      approvedBy: 'Marie Krásná',
      approvedAt: '2024-06-05',
    },
    {
      id: '2',
      staffId: '2',
      type: 'sick',
      title: 'Nemoc',
      startDate: '2024-11-20',
      endDate: '2024-11-22',
      days: 3,
      status: 'pending',
      reason: 'Chřipka s horečkou',
      requestedAt: '2024-11-19',
      approvedBy: null,
      approvedAt: null,
    },
    {
      id: '3',
      staffId: '3',
      type: 'personal',
      title: 'Osobní důvody',
      startDate: '2024-12-24',
      endDate: '2024-12-26',
      days: 3,
      status: 'approved',
      reason: 'Vánoční svátky s rodinou',
      requestedAt: '2024-11-10',
      approvedBy: 'Anna Svobodová',
      approvedAt: '2024-11-12',
    },
    {
      id: '4',
      staffId: '4',
      type: 'vacation',
      title: 'Zimní dovolená',
      startDate: '2025-02-10',
      endDate: '2025-02-17',
      days: 8,
      status: 'approved',
      reason: 'Lyžování v Alpách',
      requestedAt: '2024-10-15',
      approvedBy: 'Petr Novák',
      approvedAt: '2024-10-18',
    },
    {
      id: '5',
      staffId: '1',
      type: 'medical',
      title: 'Lékařská návštěva',
      startDate: '2024-11-25',
      endDate: '2024-11-25',
      days: 1,
      status: 'pending',
      reason: 'Preventivní prohlídka',
      requestedAt: '2024-11-20',
      approvedBy: null,
      approvedAt: null,
    },
  ];

  const filteredVacations = vacations.filter(vacation => {
    const staffMember = staff.find(s => s.id === vacation.staffId);
    const matchesSearch =
      vacation.title.toLowerCase().includes(searchTerm.toLowerCase()) ||
      vacation.reason.toLowerCase().includes(searchTerm.toLowerCase()) ||
      (staffMember && staffMember.name.toLowerCase().includes(searchTerm.toLowerCase()));
    const matchesStatus = filterStatus === 'all' || vacation.status === filterStatus;
    return matchesSearch && matchesStatus;
  });

  const getTypeColor = (type: string) => {
    switch (type) {
      case 'vacation':
        return 'bg-blue-100 text-blue-800';
      case 'sick':
        return 'bg-red-100 text-red-800';
      case 'personal':
        return 'bg-yellow-100 text-yellow-800';
      case 'medical':
        return 'bg-green-100 text-green-800';
      default:
        return 'bg-gray-100 text-gray-800';
    }
  };

  const getTypeText = (type: string) => {
    switch (type) {
      case 'vacation':
        return 'Dovolená';
      case 'sick':
        return 'Nemoc';
      case 'personal':
        return 'Osobní';
      case 'medical':
        return 'Lékařská';
      default:
        return 'Neznámý';
    }
  };

  const getStatusIcon = (status: string) => {
    switch (status) {
      case 'approved':
        return faCheckCircle;
      case 'pending':
        return faExclamationTriangle;
      case 'rejected':
        return faTimesCircle;
      default:
        return faClock;
    }
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'approved':
        return 'bg-green-100 text-green-800';
      case 'pending':
        return 'bg-yellow-100 text-yellow-800';
      case 'rejected':
        return 'bg-red-100 text-red-800';
      default:
        return 'bg-gray-100 text-gray-800';
    }
  };

  const getStatusText = (status: string) => {
    switch (status) {
      case 'approved':
        return 'Schváleno';
      case 'pending':
        return 'Čeká na schválení';
      case 'rejected':
        return 'Zamítnuto';
      default:
        return 'Neznámý';
    }
  };

  const totalVacationDays = vacations.filter(v => v.status === 'approved').reduce((sum, v) => sum + v.days, 0);

  const pendingRequests = vacations.filter(v => v.status === 'pending').length;

  return (
    <div className="h-full flex flex-col">
      <AppHeader title="Dovolené a absence" breadcrumb={breadcrumb} />

      <div className="flex-1 overflow-y-auto bg-gray-50">
        <div className="p-6 space-y-6">
          {/* Header */}
          <section className="bg-white rounded-lg shadow-sm p-6">
            <div className="flex items-center justify-between">
              <div>
                <h1 className="text-2xl font-semibold text-gray-900">Dovolené a absence</h1>
                <p className="text-gray-600 mt-1">Spravujte dovolené a absence zaměstnanců</p>
              </div>
              <div className="flex items-center space-x-3">
                <button onClick={() => setShowAddVacation(true)} className="bg-green-600 text-white px-4 py-2 rounded-lg hover:bg-green-700 transition-colors">
                  <FontAwesomeIcon icon={faPlus} className="mr-2" />
                  Nová žádost
                </button>
                <button className="border border-gray-300 px-4 py-2 rounded-lg hover:bg-gray-50 transition-colors">
                  <FontAwesomeIcon icon={faDownload} className="mr-2" />
                  Export
                </button>
              </div>
            </div>
          </section>

          {/* Stats Cards */}
          <section className="grid grid-cols-1 md:grid-cols-4 gap-6">
            <div className="bg-white rounded-lg shadow-sm p-6">
              <div className="flex items-center">
                <div className="p-3 bg-blue-50 rounded-lg">
                  <FontAwesomeIcon icon={faPlane} className="text-xl text-blue-600" />
                </div>
                <div className="ml-4">
                  <p className="text-sm font-medium text-gray-600">Celkem dnů dovolené</p>
                  <p className="text-2xl font-bold text-gray-900">{totalVacationDays}</p>
                </div>
              </div>
            </div>
            <div className="bg-white rounded-lg shadow-sm p-6">
              <div className="flex items-center">
                <div className="p-3 bg-yellow-50 rounded-lg">
                  <FontAwesomeIcon icon={faExclamationTriangle} className="text-xl text-yellow-600" />
                </div>
                <div className="ml-4">
                  <p className="text-sm font-medium text-gray-600">Čekající žádosti</p>
                  <p className="text-2xl font-bold text-gray-900">{pendingRequests}</p>
                </div>
              </div>
            </div>
            <div className="bg-white rounded-lg shadow-sm p-6">
              <div className="flex items-center">
                <div className="p-3 bg-green-50 rounded-lg">
                  <FontAwesomeIcon icon={faCheckCircle} className="text-xl text-green-600" />
                </div>
                <div className="ml-4">
                  <p className="text-sm font-medium text-gray-600">Schválené</p>
                  <p className="text-2xl font-bold text-gray-900">{vacations.filter(v => v.status === 'approved').length}</p>
                </div>
              </div>
            </div>
            <div className="bg-white rounded-lg shadow-sm p-6">
              <div className="flex items-center">
                <div className="p-3 bg-red-50 rounded-lg">
                  <FontAwesomeIcon icon={faTimesCircle} className="text-xl text-red-600" />
                </div>
                <div className="ml-4">
                  <p className="text-sm font-medium text-gray-600">Zamítnuté</p>
                  <p className="text-2xl font-bold text-gray-900">{vacations.filter(v => v.status === 'rejected').length}</p>
                </div>
              </div>
            </div>
          </section>

          {/* Search and Filters */}
          <section className="bg-white rounded-lg shadow-sm p-6">
            <div className="flex items-center space-x-4">
              <div className="flex-1 relative">
                <FontAwesomeIcon icon={faSearch} className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" />
                <input
                  type="text"
                  placeholder="Hledat žádosti o dovolenou..."
                  value={searchTerm}
                  onChange={e => setSearchTerm(e.target.value)}
                  className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-green-500/20 focus:border-green-500"
                />
              </div>
              <select
                value={filterStatus}
                onChange={e => setFilterStatus(e.target.value)}
                className="px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-green-500/20 focus:border-green-500"
              >
                <option value="all">Všechny stavy</option>
                <option value="pending">Čekající</option>
                <option value="approved">Schválené</option>
                <option value="rejected">Zamítnuté</option>
              </select>
              <button className="flex items-center px-3 py-2 border border-gray-300 rounded-lg hover:bg-gray-50">
                <FontAwesomeIcon icon={faFilter} className="w-4 h-4 mr-2" />
                Více filtrů
              </button>
            </div>
          </section>

          {/* Vacations Table */}
          <section className="bg-white rounded-lg shadow-sm">
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Zaměstnanec</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Typ</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Název</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Datum</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Dny</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Stav</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Akce</th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {filteredVacations.map(vacation => {
                    const staffMember = staff.find(s => s.id === vacation.staffId);
                    return (
                      <tr key={vacation.id} className="hover:bg-gray-50">
                        <td className="px-6 py-4 whitespace-nowrap">
                          <div className="flex items-center">
                            <div className="w-10 h-10 bg-green-100 rounded-full flex items-center justify-center">
                              <FontAwesomeIcon icon={faUser} className="w-5 h-5 text-green-600" />
                            </div>
                            <div className="ml-4">
                              <div className="text-sm font-medium text-gray-900">{staffMember?.name || 'Neznámý'}</div>
                              <div className="text-sm text-gray-500">{staffMember?.role || ''}</div>
                            </div>
                          </div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <span className={`px-2 py-1 text-xs rounded-full ${getTypeColor(vacation.type)}`}>{getTypeText(vacation.type)}</span>
                        </td>
                        <td className="px-6 py-4">
                          <div className="text-sm font-medium text-gray-900">{vacation.title}</div>
                          <div className="text-sm text-gray-500">{vacation.reason}</div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <div className="text-sm text-gray-900">{new Date(vacation.startDate).toLocaleDateString('cs-CZ')}</div>
                          <div className="text-sm text-gray-500">do {new Date(vacation.endDate).toLocaleDateString('cs-CZ')}</div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                          <div className="flex items-center">
                            <FontAwesomeIcon icon={faCalendar} className="w-4 h-4 mr-1 text-gray-400" />
                            {vacation.days} dní
                          </div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${getStatusColor(vacation.status)}`}>
                            <FontAwesomeIcon icon={getStatusIcon(vacation.status)} className="w-3 h-3 mr-1" />
                            {getStatusText(vacation.status)}
                          </span>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                          <div className="flex items-center space-x-2">
                            <button className="text-blue-600 hover:text-blue-900 p-1" title="Zobrazit detail">
                              <FontAwesomeIcon icon={faCalendar} className="w-4 h-4" />
                            </button>
                            <button className="text-gray-600 hover:text-gray-900 p-1" title="Upravit">
                              <FontAwesomeIcon icon={faEdit} className="w-4 h-4" />
                            </button>
                            <button className="text-red-600 hover:text-red-900 p-1" title="Smazat">
                              <FontAwesomeIcon icon={faTrash} className="w-4 h-4" />
                            </button>
                          </div>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </section>

          {/* Quick Actions */}
          <section className="bg-white rounded-lg shadow-sm p-6">
            <h3 className="text-lg font-medium text-gray-900 mb-4">Rychlé akce</h3>
            <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
              <button className="flex items-center justify-center p-4 border-2 border-dashed border-gray-300 rounded-lg hover:border-blue-500 hover:bg-blue-50 transition-colors">
                <FontAwesomeIcon icon={faPlane} className="w-6 h-6 text-gray-400 mr-3" />
                <span className="text-gray-600">Schválit žádosti</span>
              </button>
              <button className="flex items-center justify-center p-4 border-2 border-dashed border-gray-300 rounded-lg hover:border-green-500 hover:bg-green-50 transition-colors">
                <FontAwesomeIcon icon={faCalendar} className="w-6 h-6 text-gray-400 mr-3" />
                <span className="text-gray-600">Kalendář dovolených</span>
              </button>
              <button className="flex items-center justify-center p-4 border-2 border-dashed border-gray-300 rounded-lg hover:border-purple-500 hover:bg-purple-50 transition-colors">
                <FontAwesomeIcon icon={faUser} className="w-6 h-6 text-gray-400 mr-3" />
                <span className="text-gray-600">Zůstatky dovolené</span>
              </button>
              <button className="flex items-center justify-center p-4 border-2 border-dashed border-gray-300 rounded-lg hover:border-orange-500 hover:bg-orange-50 transition-colors">
                <FontAwesomeIcon icon={faDownload} className="w-6 h-6 text-gray-400 mr-3" />
                <span className="text-gray-600">Export reportu</span>
              </button>
            </div>
          </section>
        </div>
      </div>
    </div>
  );
};

export default VacationsPage;
